import React from 'react';
import type { Reputation, VerticalSummary, Attestation, Vertical } from '../types';
import { formatCurrency, formatHash, formatRelativeTime } from '../utils/format';

export interface HopsMarketplaceProps {
  reputation: Reputation;
  verticals: VerticalSummary[];
  attestations?: Attestation[];
  className?: string;
  onSelectVertical?: (vertical: Vertical) => void;
  onViewAttestation?: (attestation: Attestation) => void;
}

const VerticalIcons: Record<Vertical, string> = {
  hvac: '🔧',
  healthcare: '🏥',
  finance: '💰',
  legal: '⚖️',
  general: '📦',
};

const VerticalNames: Record<Vertical, string> = {
  hvac: 'HVAC',
  healthcare: 'Healthcare',
  finance: 'Finance',
  legal: 'Legal',
  general: 'General',
};

/**
 * HOPs Marketplace Panel
 * Shows tasks by vertical, reputation, attestations
 */
export function HopsMarketplace({
  reputation,
  verticals,
  attestations = [],
  className = '',
  onSelectVertical,
  onViewAttestation,
}: HopsMarketplaceProps) {
  const recentAttestations = attestations.slice(0, 5);
  const levelProgress = (reputation.score % 1) * 100;

  return (
    <div className={`bg-gray-900 border border-gray-800 rounded-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-white">HOPs Marketplace</h2>
        <a
          href="#browse"
          className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
        >
          Browse All →
        </a>
      </div>

      {/* Reputation */}
      <div className="mb-6 p-4 bg-gray-800/50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-gray-400">Your Reputation</div>
          <div className="text-sm text-gray-400">
            Score: <span className="text-white font-semibold">{reputation.score.toFixed(2)}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-2xl font-bold text-white">Level {reputation.level}</span>
          <div className="flex-1">
            <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-600 to-purple-400 transition-all duration-500"
                style={{ width: `${levelProgress}%` }}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6 text-sm text-gray-400">
          <span>
            <span className="text-white">{reputation.tasksCompleted}</span> tasks
          </span>
          <span>
            <span className="text-green-400">{(reputation.successRate * 100).toFixed(0)}%</span> success
          </span>
          <span>
            <span className="text-white">{reputation.avgResponseTime.toFixed(1)}s</span> avg response
          </span>
        </div>
      </div>

      {/* Available Tasks by Vertical */}
      <div className="mb-6">
        <div className="text-sm text-gray-400 mb-3">Available Tasks by Vertical</div>
        <div className="space-y-2">
          {verticals.map((v) => (
            <VerticalRow
              key={v.vertical}
              summary={v}
              onClick={() => onSelectVertical?.(v.vertical)}
            />
          ))}
        </div>
      </div>

      {/* Recent Attestations */}
      {recentAttestations.length > 0 && (
        <div className="border-t border-gray-800 pt-4">
          <div className="text-sm text-gray-400 mb-3">Recent Attestations</div>
          <div className="space-y-2">
            {recentAttestations.map((att) => (
              <AttestationRow
                key={att.taskId}
                attestation={att}
                onClick={() => onViewAttestation?.(att)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface VerticalRowProps {
  summary: VerticalSummary;
  onClick?: () => void;
}

function VerticalRow({ summary, onClick }: VerticalRowProps) {
  const { vertical, taskCount, rewardRange, compliance } = summary;

  return (
    <div
      className={`flex items-center justify-between p-3 bg-gray-800/30 hover:bg-gray-800/50 rounded-lg transition-colors ${
        onClick ? 'cursor-pointer' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <span className="text-lg">{VerticalIcons[vertical]}</span>
        <span className="text-white font-medium">{VerticalNames[vertical]}</span>
      </div>
      <div className="flex items-center gap-4 text-sm">
        <span className="text-gray-400">
          <span className="text-white">{taskCount}</span> tasks
        </span>
        <span className="text-green-400">
          {formatCurrency(rewardRange[0])}-{formatCurrency(rewardRange[1])}
        </span>
        {compliance && (
          <span className="px-2 py-0.5 text-xs bg-yellow-900/50 text-yellow-300 rounded">
            {compliance}
          </span>
        )}
      </div>
    </div>
  );
}

interface AttestationRowProps {
  attestation: Attestation;
  onClick?: () => void;
}

function AttestationRow({ attestation, onClick }: AttestationRowProps) {
  return (
    <div
      className={`flex items-center justify-between p-3 bg-gray-800/30 rounded-lg ${
        onClick ? 'cursor-pointer hover:bg-gray-800/50' : ''
      } transition-colors`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <code className="text-xs text-gray-400 font-mono">{attestation.taskId}</code>
        <span className="px-2 py-0.5 text-xs bg-green-900/50 text-green-300 rounded">
          COMPLETED
        </span>
      </div>
      <div className="flex items-center gap-4 text-sm">
        <span className="text-gray-500 font-mono text-xs">
          hash: {formatHash(attestation.outputHash)}
        </span>
        <span className="text-green-400 font-medium">
          {formatCurrency(attestation.reward)}
        </span>
        <span className="text-gray-500 text-xs">
          {formatRelativeTime(attestation.completedAt)}
        </span>
      </div>
    </div>
  );
}
